import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Table from "@/components/ui/table";
import type { TableColumn } from "@/types/component";
import ReportDetailLayout from "../../components/ReportDetailLayout";
import ReportFilterBar, {
  type ReportFilterState,
} from "../../components/ReportFilterBar";
import ReportKPICard from "../../components/ReportKPICard";
import ReportChart from "../../components/ReportChart";
import ReportExportButtons from "../../components/ReportExportButtons";
import { useReportData } from "../../hooks/useReportData";
import {
  reportsExtendedApi,
  type VirtualClassroomResponse,
  type VirtualClassroomItem,
  type VirtualClassroomParams,
} from "../../services/reports-extended.api";
import { Calendar, Employees, Recent } from "@/components/icons";

const PAGE_SIZE = 20;

const VirtualClassroomReportPage: React.FC = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ReportFilterState>({});
  const [page, setPage] = useState(1);

  const params: VirtualClassroomParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      dateFrom: filter.dateFrom,
      dateTo: filter.dateTo,
      search: filter.search?.trim() || undefined,
    }),
    [filter, page],
  );

  const { data, isLoading, error } = useReportData<
    VirtualClassroomResponse,
    VirtualClassroomParams
  >({
    fetcher: (p, signal) => reportsExtendedApi.getVirtualClassroom(p, signal),
    params,
  });

  const summary = data?.summary;
  const items = data?.items ?? [];
  const count = data?.count ?? 0;

  const topByAttendance = [...items]
    .sort((a, b) => b.attendanceRate - a.attendanceRate)
    .slice(0, 10);

  const columns: TableColumn<VirtualClassroomItem>[] = [
    { header: "SINIF", accessor: (row) => row.name ?? "—" },
    { header: t("reports.virtualClassroomReport.headerTraining"), accessor: (row) => row.educationTitle },
    {
      header: t("reports.virtualClassroomReport.headerStart"),
      accessor: (row) =>
        row.startDate
          ? new Date(row.startDate).toLocaleDateString("tr-TR")
          : "—",
    },
    {
      header: "OTURUM",
      accessor: (row) => row.sessionCount,
    },
    {
      header: "TOPLAM DK",
      accessor: (row) => row.totalDurationMinutes,
    },
    {
      header: "ORT. DK/OTURUM",
      accessor: (row) => row.avgSessionDurationMinutes,
    },
    {
      header: "BEKLENEN",
      accessor: (row) => row.expectedParticipants,
    },
    {
      header: "KATILAN",
      accessor: (row) => row.actualParticipants,
    },
    {
      header: "KATILIM %",
      accessor: (row) => (
        <span
          style={{
            fontWeight: 600,
            color:
              row.attendanceRate >= 70
                ? "#15803d"
                : row.attendanceRate >= 40
                  ? "#b45309"
                  : "#b91c1c",
          }}
        >
          {row.attendanceRate.toFixed(1)}%
        </span>
      ),
    },
  ];

  return (
    <ReportDetailLayout
      title={t("reports.virtualClassroomReport.title")}
      categoryLabel={t("reportCatalog.categories.operational.title")}
      description={t("reports.virtualClassroomReport.description")}
      exportSlot={
        <ReportExportButtons
          reportType="VIRTUAL_CLASSROOM"
          filters={params as unknown as Record<string, unknown>}
          filenameBase="sanal-sinif"
          exportFn={reportsExtendedApi.exportReport}
          disabled={isLoading}
        />
      }
      filterSlot={
        <ReportFilterBar
          value={filter}
          onChange={(next) => {
            setFilter(next);
            setPage(1);
          }}
          showDepartment={false}
        />
      }
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 14,
        }}
      >
        <ReportKPICard
          title={t("reports.virtualClassroomReport.totalClasses")}
          value={summary?.totalClassrooms ?? 0}
          icon={<Calendar width={18} height={18} />}
        />
        <ReportKPICard
          title="Toplam Oturum"
          value={summary?.totalSessions ?? 0}
          icon={<Recent width={18} height={18} />}
        />
        <ReportKPICard
          title={t("reports.virtualClassroomReport.totalDurationHours")}
          value={summary ? `${summary.totalDurationHours.toFixed(1)} sa` : "—"}
          icon={<Recent width={18} height={18} />}
        />
        <ReportKPICard
          title={t("reports.virtualClassroomReport.avgAttendancePct")}
          value={summary ? `${summary.avgAttendanceRate.toFixed(1)}%` : "—"}
          icon={<Employees width={18} height={18} />}
          accent={
            summary && summary.avgAttendanceRate >= 70
              ? "success"
              : summary && summary.avgAttendanceRate < 40
                ? "danger"
                : "warning"
          }
        />
      </div>

      <ReportChart
        title={t("reports.virtualClassroomReport.top10")}
        type="bar"
        isLoading={isLoading}
        isEmpty={topByAttendance.length === 0}
        series={[
          {
            name: t("reports.virtualClassroomReport.attendancePct"),
            data: topByAttendance.map((i) => i.attendanceRate),
          },
        ]}
        options={{
          xaxis: { categories: topByAttendance.map((i) => i.educationTitle) },
          plotOptions: {
            bar: { horizontal: true, borderRadius: 4, barHeight: "70%" },
          },
          colors: ["#0ea5e9"],
          yaxis: { max: 100 },
        }}
      />

      <Table
        columns={columns}
        data={items}
        isLoading={isLoading}
        getRowId={(row) => row.classroomID}
        totalRecords={count}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        emptyMessage={error ? t("reports.virtualClassroomReport.noData") : t("reports.virtualClassroomReport.noClass")}
      />
    </ReportDetailLayout>
  );
};

export default VirtualClassroomReportPage;
